// packages/web/src/components/Layout.tsx
import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../context/SubscriptionContext';
import NotificationCenter from './NotificationCenter';
import CommandPalette from './CommandPalette';
import QuickNavigator from './QuickNavigator';
import ReportIssueModal from './ReportIssueModal';
import ChatWidget from './ChatWidget';

/* ─── Types ────────────────────────────────────────────────────── */
interface NavItem {
  path: string;
  icon: string;
  label: string;
  permission?: string;
  /** Subscription feature key (e.g. 'payroll', 'expense'). Item hidden if feature not enabled. */
  feature?: string;
}

interface NavGroup {
  id: string;
  label: string;
  icon: string;
  /** If ANY permission in this array is present, group is visible.
   *  Empty = visible to everyone. */
  permissions: string[];
  /** Subscription feature key – hides entire group when feature is off */
  feature?: string;
  /** If true, this group is only visible to platform admins */
  platformOnly?: boolean;
  items: NavItem[];
}

/* ─── Sidebar Taxonomy — 8 Role-Based Groups ───────────────────── */
const NAV_GROUPS: NavGroup[] = [
  {
    id: 'platform',
    label: 'Platform Admin',
    icon: '🖥️',
    permissions: [],
    platformOnly: true,
    items: [
      { path: '/saas-admin',    icon: '🖥️', label: 'SaaS Admin' },
      { path: '/subscription',  icon: '💳', label: 'Subscriptions' },
      { path: '/billing-portal',icon: '🏦', label: 'Billing Portal' },
      { path: '/errors-reported', icon: '🐛', label: 'Errors Reported' },
    ],
  },
  {
    id: 'org-setup',
    label: 'Organization Setup',
    icon: '🏢',
    permissions: ['org:manage'],
    items: [
      { path: '/org-settings',          icon: '⚙️', label: 'Org Settings',       permission: 'org:manage' },
      { path: '/org-hierarchy',         icon: '🏢', label: 'Org Hierarchy',      permission: 'org:manage' },
      { path: '/entity-management',     icon: '🏛️', label: 'Entity Management',  permission: 'org:manage' },
      { path: '/company-policies',      icon: '📜', label: 'Company Policies',    permission: 'org:manage' },
      { path: '/dynamic-policies',      icon: '🔧', label: 'Dynamic Policies',   permission: 'org:manage' },
      { path: '/letter-templates',      icon: '✉️', label: 'Letter Templates',   permission: 'org:manage' },
      { path: '/workflows',             icon: '🔀', label: 'Workflows',           permission: 'org:manage' },
      { path: '/ctc-configurator',      icon: '💰', label: 'CTC Configurator',   permission: 'org:manage' },
      { path: '/billing-portal',        icon: '💳', label: 'Billing & Subscription', permission: 'org:manage' },
    ],
  },
  {
    id: 'access',
    label: 'Admin & Security',
    icon: '🔐',
    permissions: ['org:manage'],
    items: [
      { path: '/masters',            icon: '⚙️', label: 'Masters',              permission: 'org:manage' },
      { path: '/user-management',   icon: '🛡️', label: 'User Management',      permission: 'org:manage' },
      { path: '/access-config',     icon: '⚡', label: 'Access Configuration', permission: 'org:manage' },
      { path: '/approval-matrix',   icon: '✅', label: 'Approval Matrix',      permission: 'org:manage' },
      { path: '/pending-approvals',  icon: '📋', label: 'Pending Approvals',    permission: 'org:manage' },
      { path: '/roles-permissions', icon: '🎭', label: 'Roles & Permissions',  permission: 'org:manage' },
      { path: '/audit-logs',        icon: '🔍', label: 'Audit & Approvals',    permission: 'org:manage' },
    ],
  },
  {
    id: 'core-hr',
    label: 'Core HR & Directory',
    icon: '👥',
    permissions: [],
    items: [
      { path: '/employees',              icon: '👥', label: 'Employee Master' },
      { path: '/golden-record',          icon: '⭐', label: 'Golden Record',      permission: 'employees:manage' },
      { path: '/employee-lifecycle',     icon: '🔄', label: 'Employee Lifecycle',  permission: 'org:manage' },
      { path: '/lifecycle-flowchart',      icon: '📐', label: 'Lifecycle Flowchart',  permission: 'org:manage' },
      { path: '/lifecycle-step-viewer',  icon: '📋', label: 'Lifecycle Manager', permission: 'employees:manage' },
      { path: '/lifecycle-permissions',  icon: '🔐', label: 'Step Permissions',  permission: 'org:manage' },
      { path: '/manpower-requisition',     icon: '📋', label: 'Manpower Requisition', permission: 'employees:manage', feature: 'recruitment' },
      { path: '/interview-assessment',     icon: '🎤', label: 'Interview Assessment', permission: 'employees:manage', feature: 'recruitment' },
      { path: '/onboarding-offboarding', icon: '👋', label: 'Onboarding / Exit',  permission: 'employees:manage' },
      { path: '/workforce-management',   icon: '📈', label: 'Workforce Mgmt',     permission: 'leaves:manage' },
      { path: '/performance',            icon: '🎯', label: 'Performance',     feature: 'performance' },
      { path: '/training',              icon: '🎓', label: 'Training',        feature: 'lms' },
      { path: '/lms',                   icon: '📖', label: 'LMS / E-Learning', feature: 'lms' },
      { path: '/surveys',               icon: '📊', label: 'Surveys',         feature: 'engage' },
      { path: '/assets',                icon: '💻', label: 'Assets',              permission: 'org:manage' },
    ],
  },
  {
    id: 'lifecycle-engine',
    label: 'Lifecycle Steps (35)',
    icon: '🔄',
    permissions: [],
    items: [
      // Recruitment Phase (Steps 1-12)
      { path: '/manpower-requisition',   icon: '📋', label: '1. Manpower Requisition', permission: 'employees:manage', feature: 'recruitment' },
      { path: '/job-postings',           icon: '📝', label: '2. Job Posting',          permission: 'employees:manage', feature: 'recruitment' },
      { path: '/resume-screening',       icon: '🔍', label: '3. Resume Screening',     permission: 'employees:manage', feature: 'recruitment' },
      { path: '/interview-scheduling',   icon: '📅', label: '4. Interview Schedule',   permission: 'employees:manage', feature: 'recruitment' },
      { path: '/interview-assessment',   icon: '⭐', label: '5. Interview Assessment', permission: 'employees:manage', feature: 'recruitment' },
      { path: '/background-verification',icon: '🔐', label: '6. Background Check',    permission: 'employees:manage', feature: 'recruitment' },
      { path: '/salary-negotiation',     icon: '💼', label: '7. Salary Negotiation',   permission: 'employees:manage', feature: 'recruitment' },
      { path: '/letter-templates',       icon: '✉️', label: '8. Offer Letter',        permission: 'employees:manage' },
      { path: '/offer-acceptance',       icon: '✅', label: '9. Offer Acceptance',     permission: 'employees:manage', feature: 'recruitment' },
      { path: '/pre-joining-docs',       icon: '📂', label: '10. Pre-Joining Docs',   permission: 'employees:manage' },
      { path: '/medical-examination',    icon: '🏥', label: '11. Medical Exam',       permission: 'employees:manage' },
      { path: '/employees',              icon: '🔄', label: '12. Convert to Employee' },
      // Onboarding Phase (Step 13)
      { path: '/onboarding-offboarding', icon: '🚀', label: '13. Onboarding',         permission: 'employees:manage' },
      // Attendance Phase (Steps 14-16)
      { path: '/attendance',             icon: '📊', label: '14. Attendance' },
      { path: '/overtime-management',    icon: '⏱️', label: '15. Overtime Mgmt',      permission: 'leaves:manage' },
      { path: '/comp-off',               icon: '🔁', label: '16. Comp-Off',           permission: 'leaves:manage' },
      // Payroll Phase (Steps 17-25)
      { path: '/payroll',                icon: '🏗️', label: '17. Salary Structure',   permission: 'payroll:view', feature: 'payroll' },
      { path: '/payroll',                icon: '💵', label: '18. Salary Processing',  permission: 'payroll:view', feature: 'payroll' },
      { path: '/statutory',              icon: '🏛️', label: '19. PF/ESI/PT',         permission: 'payroll:manage', feature: 'payroll' },
      { path: '/it-declarations',        icon: '📊', label: '20. TDS & IT',           feature: 'payroll' },
      { path: '/loans',                  icon: '🏦', label: '21. Loans & Advances',   permission: 'payroll:manage', feature: 'payroll' },
      { path: '/reimbursements',         icon: '🧾', label: '22. Reimbursements',     permission: 'payroll:manage', feature: 'payroll' },
      { path: '/fbp',                    icon: '🎁', label: '23. Flexible Benefits',  permission: 'payroll:manage', feature: 'payroll' },
      { path: '/bonus-incentives',       icon: '🏆', label: '24. Bonus & Incentives', permission: 'payroll:manage', feature: 'payroll' },
      { path: '/my/payslips',            icon: '📄', label: '25. Payslips' },
      // Management Phase (Steps 26-30)
      { path: '/employee-lifecycle',     icon: '⏳', label: '26. Probation',          permission: 'employees:manage' },
      { path: '/performance',            icon: '📋', label: '27. Appraisal',          feature: 'performance' },
      { path: '/employee-lifecycle',     icon: '🚀', label: '28. Promotion',          permission: 'employees:manage' },
      { path: '/salary-revision',        icon: '💹', label: '29. Salary Revision',    permission: 'payroll:manage', feature: 'payroll' },
      { path: '/training',              icon: '🎓', label: '30. Training',            feature: 'lms' },
      // Exit Phase (Steps 31-35)
      { path: '/exit-management',        icon: '📨', label: '31. Resignation',        permission: 'employees:manage' },
      { path: '/exit-interview',        icon: '🎤', label: '32. Exit Interview',      permission: 'employees:manage' },
      { path: '/settlement',            icon: '🧮', label: '33. F&F Settlement',      permission: 'payroll:manage' },
      { path: '/letter-templates',      icon: '📜', label: '34. Relieving Letter',    permission: 'employees:manage' },
      { path: '/alumni',                icon: '🎓', label: '35. Alumni Portal',       permission: 'employees:manage' },
    ],
  },
  {
    id: 'time-att',
    label: 'Time & Attendance',
    icon: '📅',
    permissions: [],
    items: [
      { path: '/attendance',         icon: '📅', label: 'Attendance Tracker' },
      { path: '/attendance-muster',  icon: '📆', label: 'Attendance Muster' },
      { path: '/time-tracker',       icon: '⏱️', label: 'Time Tracker' },
      { path: '/leave',              icon: '🌴', label: 'Leave Tracker' },
      { path: '/leave-management',   icon: '📋', label: 'Leave Management' },
      { path: '/leave-overview',     icon: '📊', label: 'Leave Overview' },
      { path: '/holidays',           icon: '🎉', label: 'Holiday List' },
      { path: '/overtime-management', icon: '⏱️', label: 'Overtime Mgmt',    permission: 'leaves:manage' },
      { path: '/comp-off',            icon: '🔁', label: 'Comp-Off',          permission: 'leaves:manage' },
      { path: '/wfh-management',      icon: '🏠', label: 'WFH Management' },
    ],
  },
  {
    id: 'payroll',
    label: 'Payroll & Finances',
    icon: '💰',
    permissions: ['payroll:manage', 'payroll:view'],
    feature: 'payroll',
    items: [
      { path: '/payroll',              icon: '💰', label: 'Payroll Processing',   permission: 'payroll:view' },
      { path: '/payroll-management',   icon: '💵', label: 'Payroll Management',   permission: 'payroll:manage' },
      { path: '/claims',              icon: '🧾', label: 'Expense Claims',        permission: 'payroll:manage', feature: 'expense' },
      { path: '/loans',               icon: '🏧', label: 'Employee Loans',        permission: 'payroll:manage' },
      { path: '/settlement',          icon: '📦', label: 'F&F Settlement',        permission: 'payroll:manage' },
      { path: '/exit-management',      icon: '🚪', label: 'Exit Management',       permission: 'payroll:manage' },
      { path: '/alumni',               icon: '🎓', label: 'Alumni Portal',         permission: 'payroll:manage' },
      { path: '/salary-compliance',   icon: '🏦', label: 'Salary & Compliance',  permission: 'payroll:manage' },
      { path: '/statutory',           icon: '⚖️', label: 'Statutory Config',     permission: 'payroll:manage' },
      { path: '/salary-revision',     icon: '📈', label: 'Salary Revisions',    permission: 'payroll:manage' },
      { path: '/fbp',                 icon: '🎁', label: 'Flexible Benefits',   permission: 'payroll:manage' },
      { path: '/reimbursements',      icon: '🧾', label: 'Reimbursements',      permission: 'payroll:manage' },
      { path: '/form16',              icon: '📝', label: 'Form 16 / IT',          permission: 'payroll:manage' },
      { path: '/india-compliance',   icon: '🇮🇳', label: 'India Compliance',   permission: 'payroll:manage' },
    ],
  },
  {
    id: 'self-service',
    label: 'Employee Self-Service',
    icon: '🙋',
    permissions: [],
    items: [
      { path: '/my/payslips',        icon: '📄', label: 'My Payslips' },
      { path: '/it-declarations',    icon: '📋', label: 'IT Declarations & TDS' },
      { path: '/service-requests',   icon: '📨', label: 'Service Requests' },
      { path: '/helpdesk',           icon: '🎧', label: 'Helpdesk' },
      { path: '/knowledge-base',     icon: '📖', label: 'Knowledge Base' },
      { path: '/notice-board',       icon: '📌', label: 'Notice Board' },
      { path: '/engage',             icon: '💬', label: 'Engage' },
      { path: '/tasks',              icon: '✅', label: 'Tasks' },
      { path: '/my/profile',         icon: '👤', label: 'My Profile' },
      { path: '/my/team',            icon: '👥', label: 'My Team' },
      { path: '/notifications',      icon: '🔔', label: 'Notifications' },
      { path: '/skills-goals',       icon: '🧩', label: 'Skills & Goals' },
    ],
  },
  {
    id: 'insights',
    label: 'Insights & Compliance',
    icon: '📊',
    permissions: [],
    items: [
      { path: '/manager-portal',     icon: '📊', label: 'Manager Portal',      permission: 'employees:manage' },
      { path: '/reports',            icon: '📊', label: 'Reports & Gallery' },
      { path: '/reports-analytics',  icon: '📉', label: 'Analytics',           permission: 'org:manage' },
      { path: '/operations',         icon: '🌐', label: 'Operations' },
      { path: '/organization',       icon: '🏢', label: 'Organization' },
    ],
  },
];

/* ─── Component ────────────────────────────────────────────────── */
interface LayoutProps {
  children: React.ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const { user, logout, can } = useAuth();
  const { hasFeature, tier, seats } = useSubscription();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(true);
  const [reportIssueOpen, setReportIssueOpen] = useState(false);

  // Track which accordion groups are expanded (default: group containing active route)
  const activeGroupId = NAV_GROUPS.find(g =>
    g.items.some(i => location.pathname === i.path),
  )?.id;

  const [expanded, setExpanded] = useState<Record<string, boolean>>(
    activeGroupId ? { [activeGroupId]: true } : {},
  );

  const toggleGroup = useCallback((id: string) => {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const firstName = user?.profile?.first_name ?? user?.first_name ?? 'User';
  const orgName = user?.organization?.name ?? '';
  const orgLogo = user?.organization?.logo_url ?? null;
  const roleName = user?.role?.display_name ?? user?.role?.name ?? '';

  // Filter groups & items based on permissions AND subscription features
  const visibleGroups = NAV_GROUPS.map(group => {
    // Platform-only groups: only visible to platform admins
    if (group.platformOnly && !user?.is_platform_admin) {
      return null;
    }
    // Group-level visibility: if permissions array is non-empty, user must have at least one
    if (group.permissions.length > 0 && !group.permissions.some(p => can(p))) {
      return null;
    }
    // Group-level feature gate: hide entire group if feature is off
    if (group.feature && !hasFeature(group.feature)) {
      return null;
    }
    const visibleItems = group.items.filter(item => {
      // RBAC permission check
      if (item.permission && !can(item.permission)) return false;
      // Subscription feature check
      if (item.feature && !hasFeature(item.feature)) return false;
      return true;
    });
    if (visibleItems.length === 0) return null;
    return { ...group, items: visibleItems };
  }).filter(Boolean) as NavGroup[];

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#FFFFFF', color: '#2C3E50', fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" }}>

      {/* ── Sidebar ── */}
      <aside style={{
        width: open ? 252 : 64,
        background: 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)',
        borderRight: '1px solid rgba(148,163,184,0.12)',
        display: 'flex',
        flexDirection: 'column',
        transition: 'width 0.25s cubic-bezier(0.4,0,0.2,1)',
        flexShrink: 0,
        boxShadow: '2px 0 24px rgba(0,0,0,0.18)',
        zIndex: 40,
        position: 'sticky',
        top: 0,
        height: '100vh',
        overflowY: 'auto',
      }}>

        {/* ── Brand header ── */}
        <div style={{
          padding: open ? '1rem 1rem 0.75rem' : '1rem 0.5rem 0.75rem',
          borderBottom: '1px solid rgba(148,163,184,0.1)',
          display: 'flex', alignItems: 'center', gap: '0.625rem',
        }}>
          <button
            onClick={() => setOpen(v => !v)}
            aria-label={open ? 'Collapse sidebar' : 'Expand sidebar'}
            style={{
              width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'rgba(99,102,241,0.15)', border: '1px solid rgba(99,102,241,0.3)',
              borderRadius: 8, cursor: 'pointer', color: '#818cf8', fontSize: '0.9rem',
              flexShrink: 0, transition: 'background 0.15s', overflow: 'hidden', padding: 0,
            }}
          >
            {orgLogo
              ? <img src={orgLogo} alt="Logo" style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 8 }} />
              : (open ? '◁' : '▷')
            }
          </button>
          {open && (
            <div style={{ overflow: 'hidden' }}>
              <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#f1f5f9', whiteSpace: 'nowrap', letterSpacing: '-0.01em' }}>
                HR &amp; Payroll
              </div>
              {orgName && (
                <div style={{ fontSize: '0.68rem', color: '#94a3b8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginTop: 1, display: 'flex', alignItems: 'center', gap: 4 }}>
                  {orgName}
                  <span style={{
                    fontSize: '0.52rem', padding: '0px 4px', borderRadius: 4, fontWeight: 700,
                    background: tier === 'enterprise' ? 'rgba(234,179,8,0.2)' : tier === 'professional' ? 'rgba(99,102,241,0.2)' : 'rgba(148,163,184,0.15)',
                    color: tier === 'enterprise' ? '#fbbf24' : tier === 'professional' ? '#a5b4fc' : '#94a3b8',
                    textTransform: 'uppercase', letterSpacing: '0.03em',
                  }}>{tier}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Accordion Navigation ── */}
        <nav style={{
          flex: 1, padding: '0.5rem', overflowY: 'auto', overflowX: 'hidden',
          scrollbarWidth: 'thin', scrollbarColor: 'rgba(148,163,184,0.2) transparent',
        }}>
          {/* ── Dashboard (top-level) ── */}
          <button
            onClick={() => navigate('/dashboard')}
            title="Dashboard"
            style={{
              display: 'flex', alignItems: 'center', gap: '0.625rem',
              width: '100%',
              padding: open ? '0.6rem 0.625rem' : '0.6rem 0',
              justifyContent: open ? 'flex-start' : 'center',
              background: location.pathname === '/dashboard'
                ? 'linear-gradient(90deg, rgba(99,102,241,0.25) 0%, rgba(99,102,241,0.1) 100%)'
                : 'transparent',
              border: 'none',
              borderRadius: 8,
              cursor: 'pointer',
              color: location.pathname === '/dashboard' ? '#e0e7ff' : '#cbd5e1',
              fontSize: '0.85rem',
              fontWeight: location.pathname === '/dashboard' ? 700 : 500,
              marginBottom: '0.375rem',
              transition: 'all 0.15s',
            }}
          >
            <span style={{ fontSize: '1.1rem', flexShrink: 0, width: 24, textAlign: 'center' }}>🏠</span>
            {open && <span>Dashboard</span>}
          </button>
          <div style={{ height: 1, background: 'rgba(148,163,184,0.1)', margin: '0.25rem 0.5rem 0.375rem' }} />
          <div style={{ flex: 1, overflowY: 'auto',
            scrollbarWidth: 'thin', scrollbarColor: 'rgba(148,163,184,0.2) transparent',
          }}>
          {visibleGroups.map(group => {
            const isExpanded = !!expanded[group.id];
            const hasActive = group.items.some(i => location.pathname === i.path);

            return (
              <div key={group.id} style={{ marginBottom: '0.125rem' }}>
                {/* Group Header (accordion toggle) */}
                <button
                  onClick={() => open ? toggleGroup(group.id) : setOpen(true)}
                  title={group.label}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '0.625rem',
                    width: '100%', padding: open ? '0.5rem 0.625rem' : '0.5rem 0',
                    justifyContent: open ? 'flex-start' : 'center',
                    background: hasActive ? 'rgba(99,102,241,0.08)' : 'transparent',
                    border: 'none', borderRadius: 8, cursor: 'pointer',
                    color: hasActive ? '#a5b4fc' : '#94a3b8',
                    fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.04em',
                    textTransform: 'uppercase', textAlign: 'left',
                    transition: 'all 0.15s',
                  }}
                >
                  <span style={{ fontSize: '1rem', flexShrink: 0, width: 24, textAlign: 'center' }}>
                    {group.icon}
                  </span>
                  {open && (
                    <>
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {group.label}
                      </span>
                      <span style={{
                        fontSize: '0.55rem', color: '#64748b', transition: 'transform 0.2s',
                        transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        lineHeight: 1,
                      }}>
                        ▼
                      </span>
                    </>
                  )}
                </button>

                {/* Group Items (collapsible) */}
                {open && isExpanded && (
                  <div style={{
                    overflow: 'hidden',
                    paddingLeft: '0.25rem',
                    marginTop: '0.125rem',
                  }}>
                    {group.items.map(item => {
                      const active = location.pathname === item.path;
                      return (
                        <button
                          key={item.path}
                          onClick={() => navigate(item.path)}
                          title={item.label}
                          style={{
                            display: 'flex', alignItems: 'center', gap: '0.625rem',
                            width: '100%',
                            padding: '0.45rem 0.625rem 0.45rem 1.75rem',
                            marginBottom: '1px',
                            background: active
                              ? 'linear-gradient(90deg, rgba(99,102,241,0.2) 0%, rgba(99,102,241,0.08) 100%)'
                              : 'transparent',
                            border: 'none',
                            borderLeft: active ? '2px solid #6366f1' : '2px solid transparent',
                            borderRadius: '0 8px 8px 0',
                            cursor: 'pointer',
                            color: active ? '#e0e7ff' : '#cbd5e1',
                            fontSize: '0.8125rem',
                            fontWeight: active ? 600 : 400,
                            textAlign: 'left',
                            transition: 'all 0.12s',
                            whiteSpace: 'nowrap', overflow: 'hidden',
                          }}
                        >
                          <span style={{ fontSize: '0.875rem', flexShrink: 0, width: 20, textAlign: 'center', opacity: active ? 1 : 0.7 }}>
                            {item.icon}
                          </span>
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {item.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Collapsed mode: show icons only for items in active group */}
                {!open && hasActive && (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, paddingTop: 2 }}>
                    {group.items.map(item => {
                      const active = location.pathname === item.path;
                      return (
                        <button
                          key={item.path}
                          onClick={() => navigate(item.path)}
                          title={item.label}
                          style={{
                            width: 36, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
                            background: active ? 'rgba(99,102,241,0.25)' : 'transparent',
                            border: 'none', borderRadius: 6, cursor: 'pointer',
                            color: active ? '#e0e7ff' : '#94a3b8', fontSize: '0.8rem',
                            transition: 'all 0.12s',
                          }}
                        >
                          {item.icon}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
          </div>
        </nav>

        {/* ── Sidebar bottom: plan badge + seat gauge ── */}
        <div style={{ padding: '0.5rem', borderTop: '1px solid rgba(148,163,184,0.1)', textAlign: 'center' }}>
          {open && (
            <>
              {/* Tier badge */}
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '2px 8px', borderRadius: 12, fontSize: '0.62rem', fontWeight: 700,
                background: tier === 'enterprise' ? 'rgba(234,179,8,0.15)' : tier === 'professional' ? 'rgba(99,102,241,0.15)' : 'rgba(148,163,184,0.1)',
                color: tier === 'enterprise' ? '#eab308' : tier === 'professional' ? '#818cf8' : '#94a3b8',
                textTransform: 'uppercase', letterSpacing: '0.04em',
                marginBottom: 4,
              }}>
                {tier === 'enterprise' ? '⭐' : tier === 'professional' ? '💎' : '🔹'} {tier}
              </div>
              {/* Seat gauge */}
              {seats.max > 0 && (
                <div style={{ fontSize: '0.58rem', color: '#64748b', marginTop: 2 }}>
                  {seats.used}/{seats.max} seats
                  <div style={{
                    height: 3, borderRadius: 2, background: 'rgba(148,163,184,0.15)', marginTop: 3,
                    overflow: 'hidden',
                  }}>
                    <div style={{
                      height: '100%', borderRadius: 2,
                      width: `${Math.min(100, (seats.used / seats.max) * 100)}%`,
                      background: seats.remaining <= 2 ? '#ef4444' : '#6366f1',
                      transition: 'width 0.3s',
                    }} />
                  </div>
                </div>
              )}
              <div style={{ fontSize: '0.6rem', color: '#475569', opacity: 0.6, marginTop: 4 }}>HR &amp; Payroll System</div>
            </>
          )}
        </div>
      </aside>

      {/* ── Main Content ── */}
      <main style={{ flex: 1, overflowY: 'auto', minWidth: 0, background: '#f8f9fa', display: 'flex', flexDirection: 'column' }}>
        {/* ── Top Bar with Search + Notifications ── */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: '0.75rem', padding: '0.625rem 1.5rem',
          borderBottom: '1px solid #e5e7eb', background: '#fff', flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <button onClick={() => navigate('/org-hierarchy')} style={{
              display: 'flex', alignItems: 'center', gap: '0.35rem',
              padding: '0.4rem 0.75rem', background: location.pathname === '/org-hierarchy' ? '#eff6ff' : '#f8fafc',
              border: `1px solid ${location.pathname === '/org-hierarchy' ? '#bfdbfe' : '#e2e8f0'}`, borderRadius: 8,
              color: location.pathname === '/org-hierarchy' ? '#2563eb' : '#475569', fontSize: '0.78rem', fontWeight: 600,
              cursor: 'pointer', transition: 'all 0.15s', whiteSpace: 'nowrap',
            }}>🏢 Org Hierarchy</button>
            <button onClick={() => navigate('/lifecycle-flowchart')} style={{
              display: 'flex', alignItems: 'center', gap: '0.35rem',
              padding: '0.4rem 0.75rem', background: location.pathname === '/lifecycle-flowchart' ? '#f0fdf4' : '#f8fafc',
              border: `1px solid ${location.pathname === '/lifecycle-flowchart' ? '#bbf7d0' : '#e2e8f0'}`, borderRadius: 8,
              color: location.pathname === '/lifecycle-flowchart' ? '#16a34a' : '#475569', fontSize: '0.78rem', fontWeight: 600,
              cursor: 'pointer', transition: 'all 0.15s', whiteSpace: 'nowrap',
            }}>📐 Lifecycle Flow</button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <QuickNavigator />          <button
            onClick={() => document.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', ctrlKey: true }))}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.5rem',
              padding: '0.4rem 0.875rem', background: '#f1f5f9',
              border: '1px solid #e2e8f0', borderRadius: 8,
              color: '#94a3b8', fontSize: '0.8rem', cursor: 'pointer',
              fontFamily: 'inherit', transition: 'all 0.15s',
            }}
          >
            🔍 Search… <kbd style={{ fontSize: '0.65rem', background: '#e2e8f0', padding: '1px 5px', borderRadius: 4 }}>Ctrl+K</kbd>
          </button>
          <NotificationCenter />

          {/* ── User Profile + Logout (top-right) ── */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginLeft: '0.25rem', paddingLeft: '0.75rem', borderLeft: '1px solid #e2e8f0' }}>
            <button
              onClick={() => navigate('/my/profile')}
              title={`${firstName}${roleName ? ' — ' + roleName : ''}`}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem',
                padding: '0.3rem 0.625rem', background: '#f8fafc',
                border: '1px solid #e2e8f0', borderRadius: 8,
                cursor: 'pointer', transition: 'all 0.15s',
              }}
            >
              <div style={{
                width: 28, height: 28, borderRadius: '50%',
                background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', fontWeight: 700, fontSize: '0.7rem', flexShrink: 0,
              }}>
                {firstName.charAt(0).toUpperCase()}
              </div>
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#1e293b', lineHeight: 1.2 }}>{firstName}</div>
                {roleName && <div style={{ fontSize: '0.62rem', color: '#64748b', lineHeight: 1.2 }}>{roleName}</div>}
              </div>
            </button>
            <button
              onClick={logout}
              title="Logout"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 32, height: 32,
                background: 'rgba(239,68,68,0.06)',
                border: '1px solid rgba(239,68,68,0.15)',
                borderRadius: 8, cursor: 'pointer',
                color: '#ef4444', fontSize: '0.85rem',
                transition: 'all 0.15s',
              }}
            >
              🚪
            </button>
          </div>
          </div>
        </div>
        <div style={{ flex: 1, minHeight: 0 }}>
          {children}
        </div>
        {/* ── Copyright Footer with Report Error ── */}
        <footer style={{
          padding: '0.75rem 1.5rem',
          borderTop: '1px solid #e5e7eb',
          background: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontSize: '0.75rem',
          color: '#9ca3af',
          flexShrink: 0,
        }}>
          <span>© 2026 Fincella Services Private Limited. All Rights Reserved.</span>
          <button
            onClick={() => setReportIssueOpen(true)}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.35rem',
              padding: '0.35rem 0.75rem', background: '#fef2f2',
              border: '1px solid #fecaca', borderRadius: 8,
              color: '#dc2626', fontSize: '0.75rem', fontWeight: 600,
              cursor: 'pointer', transition: 'all 0.15s',
            }}
          >
            🐛 Report Error
          </button>
        </footer>
      </main>

      {/* ── Global Command Palette (Ctrl+K) ── */}
      <CommandPalette />

      {/* ── Report Issue Modal ── */}
      <ReportIssueModal isOpen={reportIssueOpen} onClose={() => setReportIssueOpen(false)} />

      {/* ── Chat with Us Widget ── */}
      <ChatWidget />
    </div>
  );
}